import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

// Lock screen / notification artwork and title (otherwise phones fall back to the tiny favicon).
export const setMediaSession = ({ title, show, artwork }) => {
  if (!('mediaSession' in navigator) || typeof window.MediaMetadata === 'undefined') return;
  navigator.mediaSession.metadata = new window.MediaMetadata({
    title,
    artist: 'The BWGELA Blogcast',
    album: show || 'The BWGELA Blogcast',
    artwork: artwork ? [{ src: artwork, sizes: '1024x1024', type: 'image/jpeg' }] : [],
  });
};

export const BlogcastAudioPlayer = ({ src, title, testid = 'blogcast-player', autoPlay = false, media = null }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => setDur(a.duration);
    const onPlay = () => { setPlaying(true); if (media) setMediaSession(media); };
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onPause);
    if (autoPlay) a.play().catch(() => {});
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onPause);
    };
  }, [src, autoPlay]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play();
    else a.pause();
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
    setCur(Number(e.target.value));
  };

  return (
    <div
      className="flex items-center gap-4 bg-[#F5F5DC] rounded-xl px-4 py-3"
      data-testid={testid}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}
        data-testid={`${testid}-toggle`}
        className="w-12 h-12 rounded-full bg-black text-[#F5F5DC] flex items-center justify-center shrink-0 hover:bg-black/80 transition-colors"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        {title && <p className="text-black text-sm font-medium mb-1 truncate">{title}</p>}
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max={dur || 0}
            step="0.1"
            value={cur}
            onChange={seek}
            data-testid={`${testid}-seek`}
            className="flex-1 h-1 accent-black cursor-pointer"
          />
          <span className="text-black/70 text-xs tabular-nums whitespace-nowrap">
            {fmt(cur)} / {fmt(dur)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BlogcastAudioPlayer;
